/**
 * Tedarikçi portalının kimlik kuralları (ADR 0112; plan `2026-10-23-tedarikci-portali.md` §2.3,
 * §2.4). Saf: I/O yok, saat parametreyle gelir. Sunucu (`apps/supplier`), ofis API'si ve worker
 * aynı değerleri buradan okur.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

/*
 * Onay PIN'i kaldırıldı (sahip kararı 2026-10-23: "pini kaldır o yerine hesap açtırıyoruz ya artık
 * o eski mantık"). Teklif gönderimi oturum açmış, e-postası doğrulanmış, etkin hesap ve açık onay
 * adımıyla yapılır (ADR 0112 §4, ADR 0111 §C). Parola kilidi ve IP sınırı sürer.
 */

/** E-posta belirteci (davet, doğrulama, parola sıfırlama) 24 saat geçerli, tek kullanımlık. */
export const SUPPLIER_TOKEN_TTL_MS = 24 * HOUR;
/** Parola en az 12 karakter (ofisle aynı; ADR 0005 §5). */
export const SUPPLIER_PASSWORD_MIN_LENGTH = 12;

export interface SupplierSessionPolicy {
  idleMs: number;
  absoluteMs: number;
}

/**
 * Oturum: boşta 30 dakika, mutlak 12 saat; "beni hatırla" yok. Ofisle aynı (mimari belge §10.3);
 * dış kullanıcı olduğu için uzatılamaz.
 */
export const SUPPLIER_SESSION_DEFAULTS: SupplierSessionPolicy = Object.freeze({
  idleMs: 30 * MINUTE,
  absoluteMs: 12 * HOUR,
});

/** Eski `pin_reset` belirteçleri tabloda kalabilir; worker onları göndermeden kapatır. */
export const SUPPLIER_TOKEN_PURPOSES = ['invite', 'verify_email', 'password_reset'] as const;
export type SupplierTokenPurpose = (typeof SUPPLIER_TOKEN_PURPOSES)[number];

/**
 * Tedarikçi hesabının şirkete bağı: davet edildi, etkin, iptal edildi (silinmez); `prospect`: açık
 * RFQ'ya teklif veren, şirkete henüz bağlı olmayan tedarikçi (ADR 0113 §4).
 */
export const SUPPLIER_LINK_STATUSES = ['invited', 'active', 'revoked', 'prospect'] as const;
export type SupplierLinkStatus = (typeof SUPPLIER_LINK_STATUSES)[number];

/**
 * Kullanım Koşulları ve Aydınlatma Metni'nin sürümü (ADR 0124; metin
 * `apps/supplier/src/web/locales/{en,tr}.ts` `legal.*`, belge `docs/hukuk/tedarikci-portali-metinleri.md`).
 * Kayıtta onaylanan sürüm hesapta saklanır; metin değişince sürüm değişir ve eski sürümü onaylamış
 * hesap girişte yeni sürümü onaylamadan portalı kullanamaz (`TERMS_UPDATE_REQUIRED`).
 */
export const SUPPLIER_TERMS_VERSION = '2026-10-24';

/**
 * Portalın veri sorumlusu (platform işletmecisi; KVKK md. 10/1-a, GDPR md. 13/1-a). Ünvan ve
 * iletişim bilgisi kodda değil, portal sunucusunun ayarındadır (`SUPPLIER_CONTROLLER_*`; ADR 0124).
 */
export interface SupplierLegalController {
  name: string;
  address: string;
  /** Başvuru ve iletişim e-postası. */
  email: string;
  /** Kayıtlı elektronik posta (KEP) adresi; isteğe bağlı. */
  kep: string | null;
  /** Sicil bilgisi (ör. MERSİS no, ticaret sicili); isteğe bağlı. */
  registry: string | null;
}

export type TokenState = 'usable' | 'expired' | 'used' | 'revoked';

/** Belirtecin durumu: kullanılmış ya da yerine yenisi verilmiş belirteç bir daha geçmez. */
export function tokenState(
  token: { expiresAt: string; usedAt: string | null; revokedAt: string | null },
  now: string,
): TokenState {
  if (token.usedAt !== null) return 'used';
  if (token.revokedAt !== null) return 'revoked';
  return Date.parse(now) < Date.parse(token.expiresAt) ? 'usable' : 'expired';
}
